import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowDown, Lock, Mail, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ ANIMATION VARIANTS ============
const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 1, ease: "easeOut" }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.95 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const slideInLeft = {
  initial: { opacity: 0, x: -60 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const slideInRight = {
  initial: { opacity: 0, x: 60 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

// Container for staggered children
const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  },
  viewport: { once: true, margin: "-100px" }
};

const staggerItem = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

// Text reveal animation
const textReveal = {
  initial: { opacity: 0, y: 100 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
};

// Line draw animation
const lineGrow = {
  initial: { scaleX: 0 },
  whileInView: { scaleX: 1 },
  viewport: { once: true },
  transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] }
};

// Six Worlds imagery
const WORLD_IMAGES = {
  metals: "https://images.unsplash.com/photo-1624295329185-13a841e9a268?auto=format&fit=crop&w=800&q=80",
  tcg: "https://images.unsplash.com/photo-1611931960487-4932667079f1?auto=format&fit=crop&w=800&q=80",
  sports: "https://images.pexels.com/photos/7783409/pexels-photo-7783409.jpeg?auto=compress&cs=tinysrgb&w=800",
  watches: "https://images.unsplash.com/photo-1788125856699-ddd59e4f24c6?auto=format&fit=crop&w=800&q=80",
  cars: "https://images.unsplash.com/photo-1691229732670-f7034e54d2cb?auto=format&fit=crop&w=800&q=80",
  treasures: "https://images.pexels.com/photos/14950665/pexels-photo-14950665.jpeg?auto=compress&cs=tinysrgb&w=800"
};

// ============ NAVIGATION ============
const Navigation = () => (
  <motion.nav 
    className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 md:px-10 py-4 sm:py-6 bg-gradient-to-b from-void via-void/90 to-transparent"
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.5 }}
  >
    <div className="flex justify-between items-center max-w-7xl mx-auto">
      <Link to="/" className="font-cinzel text-sm text-parchment/70 hover:text-parchment transition-colors tracking-wider">
        Sovereign
      </Link>
      <div className="flex items-center gap-4 sm:gap-6 md:gap-8">
        <Link 
          to="/partners" 
          className="font-manrope text-[9px] sm:text-[10px] tracking-[0.15em] text-stone-500 hover:text-gold-500 transition-colors uppercase hidden sm:block"
        >
          Partners
        </Link>
        <Link 
          to="/the-room" 
          className="font-manrope text-[9px] sm:text-[10px] tracking-[0.12em] sm:tracking-[0.15em] px-3 sm:px-4 py-2 border border-gold-500/40 text-gold-500 hover:bg-gold-500 hover:text-charcoal transition-all uppercase"
        >
          The Room
        </Link>
      </div>
    </div>
  </motion.nav>
);

// ============ HERO SECTION ============
const HeroSection = () => (
  <section className="min-h-[100svh] flex flex-col items-center justify-center px-4 sm:px-6 pt-20 relative">
    <motion.div 
      className="text-center max-w-5xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
    >
      <motion.p 
        className="font-manrope text-[9px] sm:text-[10px] md:text-[11px] tracking-[0.3em] sm:tracking-[0.4em] text-gold-500 uppercase mb-6 sm:mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        Where Serious Markets Converge
      </motion.p>
      
      <motion.h1 
        className="font-cinzel text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-wider mb-8 sm:mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        SOVEREIGN
      </motion.h1>

      <motion.p 
        className="font-cormorant text-lg sm:text-xl md:text-2xl text-stone-400 italic max-w-2xl mx-auto mb-10 sm:mb-16 px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
        A curated network built around the people, assets and opportunities shaping the world's most important collectible markets.
      </motion.p>

      {/* First Room Introduction */}
      <motion.div
        className="border border-stone-800 bg-stone-900/30 p-6 sm:p-8 md:p-10 max-w-xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
      >
        <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-3 sm:mb-4">
          The Founding Experience
        </p>
        <h2 className="font-cinzel text-2xl sm:text-3xl md:text-4xl text-parchment mb-4 sm:mb-6">THE ROOM</h2>
        <p className="font-manrope text-xs sm:text-sm text-stone-500 mb-2">PRECIOUS METALS + NUMISMATICS</p>
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-stone-400 mb-6 sm:mb-8">
          <span className="font-manrope text-xs sm:text-sm">LAS VEGAS</span>
          <span className="w-1 h-1 rounded-full bg-gold-500" />
          <span className="font-manrope text-xs sm:text-sm">DECEMBER 11-12, 2026</span>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link 
            to="/the-room"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 bg-gold-500 text-charcoal font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:bg-gold-400 transition-all"
            data-testid="hero-explore-room-btn"
          >
            Explore The Room
            <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
          </Link>
          <Link 
            to="/the-room"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 border border-stone-600 text-stone-300 font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:border-gold-500 hover:text-gold-500 transition-all"
          >
            Request Access
          </Link>
        </div>
      </motion.div>
    </motion.div>

    {/* Scroll indicator */}
    <motion.div 
      className="absolute bottom-6 sm:bottom-10 hidden sm:block"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.5 }}
    >
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <ArrowDown className="w-5 h-5 text-stone-600" />
      </motion.div>
    </motion.div>
  </section>
);

// ============ WHY SOVEREIGN SECTION ============
const WhySovereignSection = () => (
  <section className="py-12 sm:py-20 md:py-28 border-t border-stone-800 overflow-hidden">
    <div className="max-w-5xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-8 sm:mb-12 md:mb-16">
        <motion.h2 
          className="font-cinzel text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-parchment leading-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          THE RIGHT ROOM
        </motion.h2>
        <motion.h2 
          className="font-cinzel text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-gold-500 leading-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        >
          CHANGES EVERYTHING.
        </motion.h2>
      </div>

      <motion.div 
        className="max-w-3xl mx-auto mb-8 sm:mb-12"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <p className="font-manrope text-xs sm:text-sm md:text-base text-stone-400 leading-relaxed text-center mb-6 sm:mb-8">
          Collectible markets are changing.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 text-center">
          {[
            "Collectors become investors.",
            "Sellers become creators.",
            "Creators become brands.",
            "Dealers become distributors.",
            "Communities become markets.",
            "Categories increasingly overlap."
          ].map((text, i) => (
            <p 
              key={i}
              className="font-manrope text-[10px] sm:text-xs md:text-sm text-stone-500"
            >
              {text}
            </p>
          ))}
        </div>
      </motion.div>

      <motion.div 
        className="text-center max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <p className="font-manrope text-xs sm:text-sm md:text-base text-stone-400 leading-relaxed mb-4 sm:mb-6">
          Yet many of the most valuable relationships still exist across disconnected platforms, communities and private networks.
        </p>
        <p className="font-cormorant text-lg sm:text-xl md:text-2xl lg:text-3xl text-parchment italic">
          Sovereign brings the right people closer together.<br className="hidden sm:block" />
          Not through mass attendance. Through curation.
        </p>
      </motion.div>
    </div>
  </section>
);

// ============ ACCESS IS THE PRODUCT SECTION ============
const AccessSection = () => (
  <section className="py-12 sm:py-20 md:py-28 border-t border-stone-800 bg-gradient-to-b from-stone-900/30 to-void">
    <div className="max-w-5xl mx-auto px-4 sm:px-6">
      <motion.div 
        className="text-center mb-8 sm:mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
      >
        <p className="font-cormorant text-xl sm:text-2xl md:text-3xl lg:text-4xl text-parchment leading-tight mb-1 sm:mb-2">
          Access is the product.
        </p>
        <p className="font-cormorant text-xl sm:text-2xl md:text-3xl lg:text-4xl text-gold-500 leading-tight">
          Curation is the discipline.
        </p>
      </motion.div>

      <motion.p 
        className="font-manrope text-xs sm:text-sm md:text-base text-stone-400 text-center max-w-2xl mx-auto mb-8 sm:mb-12"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        Sovereign is designed around access to:
      </motion.p>

      <motion.div 
        className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3 mb-8 sm:mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {["PEOPLE", "ASSETS", "OPPORTUNITIES", "INFORMATION", "RELATIONSHIPS", "MARKETS"].map((item, i) => (
          <div 
            key={i}
            className="border border-stone-800 p-3 sm:p-4 md:p-6 text-center hover:border-gold-500/30 hover:bg-gold-500/5 transition-all duration-300"
          >
            <p className="font-manrope text-[8px] sm:text-[10px] md:text-xs tracking-wider text-stone-400">{item}</p>
          </div>
        ))}
      </motion.div>

      <motion.p 
        className="font-manrope text-[10px] sm:text-xs md:text-sm text-stone-500 text-center max-w-xl mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        The goal is not simply to create events. The goal is to build a trusted network across important collectible markets.
      </motion.p>
    </div>
  </section>
);

// ============ SIX WORLDS SECTION ============
const SixWorldsSection = () => {
  const worlds = [
    {
      number: "01",
      name: "PRECIOUS METALS + NUMISMATICS",
      subtitle: "The Founding Room",
      description: "Rare coins. Gold. Silver. Currency. Bullion. Numismatics.",
      location: "LAS VEGAS",
      date: "DECEMBER 11-12, 2026",
      status: "live",
      image: WORLD_IMAGES.metals,
      link: "/the-room"
    },
    {
      number: "02",
      name: "TCG",
      subtitle: "Trading Card Games",
      description: "Pokemon. Magic. Yu-Gi-Oh!. One Piece. Rare cards. Important collections.",
      status: "coming",
      image: WORLD_IMAGES.tcg
    },
    {
      number: "03",
      name: "SPORTS CARDS + MEMORABILIA",
      subtitle: "Sports Collectibles",
      description: "Cards. Autographs. Game-used pieces. Historic memorabilia. Important collections.",
      status: "coming",
      image: WORLD_IMAGES.sports
    },
    {
      number: "04",
      name: "LUXURY WATCHES + JEWELRY",
      subtitle: "Timeless Assets",
      description: "Important watches. Rare timepieces. Fine jewelry. Gemstones. Exceptional craftsmanship.",
      status: "coming",
      image: WORLD_IMAGES.watches
    },
    {
      number: "05",
      name: "COLLECTIBLE CARS + VEHICLES",
      subtitle: "Automotive Icons",
      description: "Collector cars. Exotic vehicles. Historic automobiles. Motorcycles. Automotive icons.",
      status: "coming",
      image: WORLD_IMAGES.cars
    },
    {
      number: "06",
      name: "HIGH-END COLLECTIBLES + TREASURES",
      subtitle: "Beyond Categories",
      description: "Rare objects. Historic artifacts. Entertainment memorabilia. Pop culture. One-of-one pieces.",
      status: "coming",
      image: WORLD_IMAGES.treasures
    }
  ];

  return (
    <section className="py-12 sm:py-20 md:py-28 border-t border-stone-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Heading */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <motion.p 
            className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-stone-600 uppercase mb-3 sm:mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            The Sovereign Collection
          </motion.p>
          <motion.h2 
            className="font-cinzel text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-parchment mb-1 sm:mb-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            SIX WORLDS.
          </motion.h2>
          <motion.h2 
            className="font-cinzel text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-gold-500"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            ONE NETWORK.
          </motion.h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {worlds.map((world, i) => (
            <motion.div
              key={i}
              className={`relative group overflow-hidden border transition-all duration-300 hover:-translate-y-2 ${
                world.status === "live" 
                  ? "border-gold-500/40 hover:border-gold-500/60" 
                  : "border-stone-700 hover:border-stone-600"
              }`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Image with parallax-like effect */}
              <motion.div 
                className="relative h-48 sm:h-56 md:h-64 overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
              >
                <img 
                  src={world.image} 
                  alt={world.name}
                  className={`w-full h-full object-cover transition-all duration-500 ${
                    world.status === "live" 
                      ? "opacity-60 group-hover:opacity-75" 
                      : "opacity-40 grayscale-[70%] group-hover:opacity-50 group-hover:grayscale-[50%]"
                  }`}
                />
                <div className={`absolute inset-0 ${
                  world.status === "live"
                    ? "bg-gradient-to-t from-void via-void/60 to-transparent"
                    : "bg-gradient-to-t from-void via-void/50 to-void/20"
                }`} />
                
                {/* Number badge */}
                <div className="absolute top-4 left-4">
                  <span className={`font-manrope text-[10px] tracking-wider ${
                    world.status === "live" ? "text-gold-500" : "text-stone-400"
                  }`}>
                    {world.number}
                  </span>
                </div>

                {/* Status badge */}
                <div className="absolute top-4 right-4">
                  <span className={`font-manrope text-[9px] sm:text-[10px] tracking-[0.15em] uppercase px-2 py-1 ${
                    world.status === "live" 
                      ? "bg-gold-500 text-charcoal" 
                      : "border border-stone-600 text-stone-400 bg-void/50"
                  }`}>
                    {world.status === "live" ? "LIVE" : "COMING SOON"}
                  </span>
                </div>
              </motion.div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                <p className={`font-manrope text-[9px] sm:text-[10px] tracking-wider uppercase mb-2 ${
                  world.status === "live" ? "text-stone-500" : "text-stone-500"
                }`}>
                  {world.subtitle}
                </p>
                <h3 className={`font-cinzel text-base sm:text-lg mb-3 ${
                  world.status === "live" ? "text-parchment" : "text-stone-300"
                }`}>
                  {world.name}
                </h3>
                <p className={`font-manrope text-[10px] sm:text-xs leading-relaxed mb-4 ${
                  world.status === "live" ? "text-stone-500" : "text-stone-400"
                }`}>
                  {world.description}
                </p>

                {world.status === "live" ? (
                  <>
                    <div className="flex items-center gap-2 text-stone-400 text-xs mb-4">
                      <span>{world.location}</span>
                      <span className="w-1 h-1 rounded-full bg-gold-500" />
                      <span>{world.date}</span>
                    </div>
                    <Link 
                      to={world.link}
                      className="inline-flex items-center gap-2 font-manrope text-[10px] sm:text-xs tracking-wider text-gold-500 hover:text-gold-400 transition-colors uppercase"
                    >
                      Explore The Room
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  </>
                ) : (
                  <p className="font-manrope text-[10px] sm:text-xs text-stone-500 uppercase tracking-wider">
                    Announcement Coming
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ WHO IS SOVEREIGN FOR SECTION ============
const WhoSection = () => {
  const types = [
    "COLLECTORS", "BUYERS", "SELLERS", "DEALERS", "CREATORS",
    "DISTRIBUTORS", "REPACKERS", "FOUNDERS", "BRANDS", "INVESTORS", "MARKET MAKERS"
  ];

  return (
    <section className="py-12 sm:py-20 md:py-28 border-t border-stone-800 bg-gradient-to-b from-stone-900/20 to-void">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div 
          className="text-center mb-6 sm:mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-stone-600 uppercase mb-3 sm:mb-4">
            The Network
          </p>
          <h2 className="font-cinzel text-xl sm:text-2xl md:text-4xl lg:text-5xl text-parchment leading-tight">
            BUILT FOR PEOPLE WHO TAKE<br />
            <span className="text-gold-500">THE MARKET SERIOUSLY.</span>
          </h2>
        </motion.div>

        <motion.div 
          className="flex flex-wrap justify-center gap-1.5 sm:gap-2 md:gap-3 mb-6 sm:mb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {types.map((type, i) => (
            <span 
              key={i}
              className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 border border-stone-800 font-manrope text-[8px] sm:text-[10px] md:text-xs tracking-wider text-stone-400 hover:border-gold-500/30 hover:text-gold-500 transition-all"
            >
              {type}
            </span>
          ))}
        </motion.div>

        <motion.div 
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p className="font-manrope text-[10px] sm:text-xs md:text-sm text-stone-400 leading-relaxed mb-3 sm:mb-4">
            Some move millions of dollars in product. Some command enormous audiences. Some control inventory or distribution.
          </p>
          <p className="font-manrope text-[10px] sm:text-xs md:text-sm text-stone-500 leading-relaxed mb-4 sm:mb-6">
            And some are simply serious collectors who want to be closer to the people, assets and opportunities driving the market.
          </p>
          <p className="font-cormorant text-lg sm:text-xl md:text-2xl lg:text-3xl text-parchment italic">
            They share one thing: They take the market seriously.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ============ CURATED NOT CLOSED SECTION ============
const CuratedSection = () => (
  <section className="py-12 sm:py-20 md:py-28 border-t border-stone-800">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
      <motion.p 
        className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-3 sm:mb-4"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
      >
        The Philosophy
      </motion.p>
      
      <motion.h2 
        className="font-cinzel text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-parchment mb-6 sm:mb-8"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
      >
        CURATED, NOT CLOSED.
      </motion.h2>
      
      <motion.div 
        className="max-w-2xl mx-auto mb-6 sm:mb-10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <p className="font-manrope text-xs sm:text-sm md:text-base text-stone-400 leading-relaxed mb-3 sm:mb-4">
          Sovereign isn't about creating the largest room. It's about creating the right one.
        </p>
        <p className="font-manrope text-xs sm:text-sm md:text-base text-stone-500 leading-relaxed mb-4 sm:mb-6">
          Attendance and participation are intentionally limited so the quality of the network remains high.
        </p>
        <p className="font-cormorant text-base sm:text-lg md:text-xl lg:text-2xl text-parchment italic">
          Anyone who believes they belong should feel comfortable requesting access.
        </p>
      </motion.div>

      <motion.div 
        className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {["Applications are reviewed.", "Access is limited.", "Approved applicants receive private access."].map((text, i) => (
          <p 
            key={i} 
            className="font-manrope text-[9px] sm:text-[10px] md:text-xs text-stone-500"
          >
            {text}
          </p>
        ))}
      </motion.div>

      <motion.div 
        className="flex flex-col sm:flex-row items-center justify-center gap-3"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <Link 
          to="/the-room"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 bg-gold-500 text-charcoal font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:bg-gold-400 transition-all"
          data-testid="request-access-btn"
        >
          Request Access
          <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
        </Link>
        <Link 
          to="/the-room#nominate"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 border border-stone-600 text-stone-300 font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:border-gold-500 hover:text-gold-500 transition-all"
        >
          Nominate Someone
        </Link>
      </motion.div>
    </div>
  </section>
);

// ============ THE NETWORK BECOMES REAL ============
const NetworkRealSection = () => (
  <section className="py-12 sm:py-20 md:py-28 border-t border-stone-800 bg-gradient-to-b from-void to-stone-900/30">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
      <motion.p 
        className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-stone-600 uppercase mb-4 sm:mb-6"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
      >
        The First Experience
      </motion.p>
      
      <motion.h2 
        className="font-cinzel text-lg sm:text-2xl md:text-3xl text-stone-500 mb-1 sm:mb-2"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
      >
        THE NETWORK BECOMES REAL.
      </motion.h2>
      <motion.h2 
        className="font-cinzel text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-parchment mb-4 sm:mb-6"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        THE ROOM
      </motion.h2>
      
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <p className="font-manrope text-[10px] sm:text-xs text-gold-500 tracking-wider mb-1 sm:mb-2">
          PRECIOUS METALS + NUMISMATICS
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-stone-400 mb-2 sm:mb-3">
          <span className="font-manrope text-xs sm:text-sm">LAS VEGAS</span>
          <span className="w-1 h-1 rounded-full bg-gold-500" />
          <span className="font-manrope text-xs sm:text-sm">DECEMBER 11-12, 2026</span>
        </div>
        <p className="font-manrope text-[10px] sm:text-xs text-stone-500 mb-4 sm:mb-6">
          200-300 CURATED ATTENDEES
        </p>
      </motion.div>

      <motion.p 
        className="font-cormorant text-base sm:text-lg md:text-xl text-stone-300 italic max-w-2xl mx-auto mb-6 sm:mb-8"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        Two days built around the people who collect, buy, sell, create, distribute and move the market.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <Link 
          to="/the-room"
          className="inline-flex items-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-gold-500 text-charcoal font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:bg-gold-400 transition-all"
          data-testid="explore-room-btn"
        >
          Explore The Room
          <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
        </Link>
      </motion.div>
    </div>
  </section>
);

// ============ ACCESS LOOKUP ============
const AccessLookupSection = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAccessLookup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/access/lookup?email=${encodeURIComponent(email)}`);
      if (response.data.found) {
        navigate(`/access/${response.data.access_token}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 border-t border-stone-800 bg-stone-900/30">
      <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
        <div className="inline-flex items-center justify-center w-10 sm:w-12 h-10 sm:h-12 border border-stone-700 rounded-full mb-4 sm:mb-6">
          <Lock className="w-4 sm:w-5 h-4 sm:h-5 text-stone-500" />
        </div>
        
        <h3 className="font-cinzel text-lg sm:text-xl text-parchment mb-2">Already Have Access?</h3>
        <p className="font-manrope text-xs sm:text-sm text-stone-500 mb-6 sm:mb-8">
          Enter your email to continue
        </p>
        
        <form onSubmit={handleAccessLookup} className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <div className="flex-1 relative">
            <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full pl-10 sm:pl-11 pr-4 py-3 bg-void border border-stone-700 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:outline-none focus:border-gold-500 transition-colors"
              data-testid="home-access-email"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-stone-800 text-parchment font-manrope text-xs tracking-wider uppercase hover:bg-gold-600 transition-colors disabled:opacity-50"
            data-testid="home-access-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Access"}
          </button>
        </form>
      </div>
    </section>
  );
};

// ============ FINAL CTA ============
const FinalCTASection = () => (
  <section className="py-12 sm:py-20 md:py-28 border-t border-stone-800">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
      <motion.h2 
        className="font-cinzel text-xl sm:text-2xl md:text-4xl lg:text-5xl text-parchment mb-1 sm:mb-2"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6 }}
      >
        THE RIGHT ROOM
      </motion.h2>
      <motion.h2 
        className="font-cinzel text-xl sm:text-2xl md:text-4xl lg:text-5xl text-gold-500 mb-6 sm:mb-8"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        CHANGES EVERYTHING.
      </motion.h2>

      <motion.div 
        className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-stone-400 mb-6 sm:mb-8"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <span className="font-manrope text-xs sm:text-sm tracking-wider">LAS VEGAS</span>
        <span className="w-1 h-1 rounded-full bg-gold-500" />
        <span className="font-manrope text-xs sm:text-sm tracking-wider">DECEMBER 11-12, 2026</span>
      </motion.div>

      <motion.div 
        className="flex flex-col sm:flex-row items-center justify-center gap-3"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <Link 
          to="/the-room"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-gold-500 text-charcoal font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:bg-gold-400 transition-all"
          data-testid="final-enter-room-btn"
        >
          Enter The Room
          <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
        </Link>
        <Link 
          to="/the-room"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-3 sm:py-4 border border-stone-600 text-stone-300 font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:border-gold-500 hover:text-gold-500 transition-all"
        >
          Request Access
        </Link>
      </motion.div>
    </div>
  </section>
);

// ============ FOOTER ============
const Footer = () => (
  <footer className="py-10 sm:py-12 border-t border-stone-800">
    <div className="max-w-5xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <p className="font-cinzel text-sm text-stone-600 tracking-wider">Sovereign</p>
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
          <Link to="/the-room" className="font-manrope text-[10px] text-stone-600 hover:text-parchment transition-colors uppercase tracking-wider">
            The Room
          </Link>
          <Link to="/partners" className="font-manrope text-[10px] text-stone-600 hover:text-parchment transition-colors uppercase tracking-wider">
            Partners
          </Link>
          <Link to="/investors" className="font-manrope text-[10px] text-stone-600 hover:text-parchment transition-colors uppercase tracking-wider">
            Investors
          </Link>
          <a href="mailto:info@sovereigngroup.io" className="font-manrope text-[10px] text-stone-600 hover:text-parchment transition-colors uppercase tracking-wider">
            Contact
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// ============ MAIN COMPONENT ============
const SovereignHomePage = () => {
  return (
    <main className="bg-void min-h-screen text-parchment">
      <Navigation />
      <HeroSection />
      <WhySovereignSection />
      <AccessSection />
      <SixWorldsSection />
      <WhoSection />
      <CuratedSection />
      <NetworkRealSection />
      <AccessLookupSection />
      <FinalCTASection />
      <Footer />
    </main>
  );
};

export default SovereignHomePage;
